// Consent Manager - Handles cookie consent state and analytics loading
const CONSENT_KEY = 'pa_consent'
const USER_ID_KEY = 'pa_user_id'

// Generate a random user ID for consent logging (no PII)
export const getUserId = () => {
  let userId = localStorage.getItem(USER_ID_KEY)
  if (!userId) {
    userId = 'user_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    localStorage.setItem(USER_ID_KEY, userId)
  }
  return userId
}

// Get current consent status
export const getConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY)
    if (!stored) return null
    return JSON.parse(stored)
  } catch (err) {
    console.error('Error reading consent:', err)
    return null
  }
}

// Save consent decision
export const saveConsent = (status) => {
  const consent = {
    status, // 'accepted' | 'rejected'
    timestamp: new Date().toISOString(),
    policyVersion: '1.0', // Can be dynamic from Legal module
    countryCode: 'PE',
    userId: getUserId()
  }
  
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent))
    return consent
  } catch (err) {
    console.error('Error saving consent:', err)
    return null
  }
}

// Clear consent (for resetting)
export const clearConsent = () => {
  localStorage.removeItem(CONSENT_KEY)
}

// Update Google Consent Mode
export const updateGoogleConsent = (granted) => {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted'
    })
  }
}

// Load Google Analytics
export const loadGoogleAnalytics = (measurementId) => {
  try {
    if (!measurementId) {
      console.warn('GA Measurement ID not provided')
      return
    }

    // Check if already loaded
    if (window.GA_LOADED) return
    
    // Load gtag.js
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    document.head?.appendChild(script)

    // Initialize gtag
    window.dataLayer = window.dataLayer || []
    window.gtag = function() { window.dataLayer.push(arguments) }
    window.gtag('js', new Date())
    window.gtag('config', measurementId, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure'
    })

    window.GA_LOADED = true
  } catch (e) {
    console.error('loadGoogleAnalytics error:', e)
  }
}

// Load ContentSquare
export const loadContentSquare = () => {
  try {
    // Skip on auth pages to prevent sandboxed iframe errors
    const path = window.location.pathname
    if (path.startsWith('/login') || path.startsWith('/admin')) return

    // Check if already loaded
    if (window.CS_LOADED || document.querySelector('script[src*="contentsquare.net"]')) return
    
    const script = document.createElement('script')
    script.src = 'https://t.contentsquare.net/uxa/722a711b7b8d6.js'
    script.async = true
    document.head?.appendChild(script)
    
    window.CS_LOADED = true
  } catch (e) {
    console.error('loadContentSquare error:', e)
  }
}

// Opt out of ContentSquare
export const optOutContentSquare = () => {
  // Initialize _uxa array if needed
  window._uxa = window._uxa || []
  
  // Push opt-out command
  window._uxa.push(['optout'])
  
  // Set opt-out cookie
  document.cookie = '_cs_optout=true;path=/;max-age=31536000;SameSite=Lax'
}

// Remove Google Analytics cookies
export const removeGACookies = () => {
  const cookies = ['_ga', '_gid', '_gat', '_gat_gtag_' + (import.meta.env.VITE_GA_ID || '').replace(/-/g, '_')]
  cookies.forEach(name => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`
  })
}

// Initialize consent on app start
export const initConsent = () => {
  try {
    const consent = getConsent()
    
    // Cleanup ContentSquare on auth pages to prevent interference
    const path = window.location.pathname
    if (path.startsWith('/login') || path.startsWith('/admin')) {
      document.querySelectorAll('script[src*="contentsquare.net"]').forEach(s => s.parentNode?.removeChild(s))
      try { delete window._uxa } catch {}
      try { window.CS_LOADED = false } catch {}
    }
    
    if (consent?.status === 'accepted') {
      // User previously accepted - load analytics
      updateGoogleConsent(true)
      try { loadGoogleAnalytics(import.meta.env.VITE_GA_ID) } catch (e) { console.error('GA load error:', e) }
      // Disabled: try { loadContentSquare() } catch (e) { console.error('CS load error:', e) }
    } else if (consent?.status === 'rejected') {
      // User previously rejected - ensure opt-out
      updateGoogleConsent(false)
      try { optOutContentSquare() } catch (e) { console.error('CS optout error:', e) }
    }
    // If no consent yet, defaults remain (denied)
  } catch (e) {
    console.error('initConsent error:', e)
  }
}

// Handle accept action
export const acceptConsent = () => {
  try {
    saveConsent('accepted')
    updateGoogleConsent(true)
    loadGoogleAnalytics(import.meta.env.VITE_GA_ID)
    // Disabled: loadContentSquare()
  } catch (e) {
    console.error('acceptConsent error:', e)
  }
}

// Handle reject action
export const rejectConsent = () => {
  try {
    saveConsent('rejected')
    updateGoogleConsent(false)
    optOutContentSquare()
    removeGACookies()
  } catch (e) {
    console.error('rejectConsent error:', e)
  }
}
